/* Shared search guidance. Identifies query intent, never system compatibility.
   Searchanise owns its results; this component only offers independent help links. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root || !root.document || root.CQSearchHelp) return;
  root.CQSearchHelp = api;
  root.document.addEventListener('submit', (event) => {
    if (!event.target.matches('form[data-cq-search-normalize]')) return;
    const input = event.target.querySelector('[name="q"]');
    if (input) input.value = api.rewrite(input.value);
  }, true);
  if (root.customElements.get('cq-search-help')) return;

  class SearchHelp extends root.HTMLElement {
    connectedCallback() {
      this.update = this.update.bind(this);
      this.onRoute = () => this.update(api.queryFromURL(root.location.href));
      this.update(this.dataset.mode === 'overlay' ? '' : (this.dataset.query || api.queryFromURL(root.location.href)));
      if (this.dataset.mode === 'overlay') return;
      root.addEventListener('popstate', this.onRoute);
      root.addEventListener('hashchange', this.onRoute);
      // Searchanise changes the URL and replaces its result DOM without a page load.
      // Do not wrap history methods or modify the app's search input/result links.
      if (this.dataset.mode === 'searchanise') {
        this.observer = new root.MutationObserver(() => {
          const query = api.queryFromURL(root.location.href);
          if (query !== this.query) this.update(query);
        });
        this.observer.observe(this.parentElement, { childList: true, subtree: true });
      }
    }
    disconnectedCallback() {
      root.removeEventListener('popstate', this.onRoute);
      root.removeEventListener('hashchange', this.onRoute);
      this.observer?.disconnect();
    }
    update(query) {
      this.query = String(query || '');
      const intent = api.intentForQuery(this.query);
      this.hidden = !intent;
      this.querySelectorAll('[data-search-help-kind]').forEach((panel) => {
        panel.hidden = panel.dataset.searchHelpKind !== intent;
      });
      const lookup = this.querySelector('[data-search-part-lookup]');
      const normalized = api.normalizePartQuery(this.query);
      const finder = this.querySelector('[data-search-replacement-finder]');
      if (finder) {
        // Carry the shopper's words into the existing finder, keeping its locale
        // and compatibility confirmation. Searchanise's own search stays intact.
        const destination = new URL(finder.href, root.location.href);
        if (normalized) destination.searchParams.set('q', normalized.slice(0, 100));
        else destination.searchParams.delete('q');
        finder.href = destination.pathname + destination.search;
      }
      // A direct results-page visit may still contain an abbreviated SKU. Offer
      // the canonical search explicitly, without rewriting Searchanise's query.
      if (lookup) {
        lookup.hidden = this.dataset.mode === 'overlay' || !api.isPartQuery(this.query) || normalized === this.query.trim();
        lookup.textContent = (lookup.dataset.label || '').replace('__CODE__', normalized);
        lookup.href = this.dataset.searchUrl + '?q=' + encodeURIComponent(normalized) + '&options%5Bprefix%5D=last';
      }
    }
  }
  root.customElements.define('cq-search-help', SearchHelp);
})(typeof window !== 'undefined' ? window : null, function () {
  // Only complete codes are expanded: a catalog SKU prefix (CQE-XX-00000, the prefixes the catalog uses, read
  // 2026-10-14) with its five digits. Shoppers type them without CQE (ct-00133, pt-03051) or with the letters swapped
  // (qce-rc-04000, PostHog /search queries Oct 2026), so those forms expand too. Bare numbers, partial codes and prose
  // stay intact.
  const partPattern = /^(?:(?:CQE|QCE|CEQ|CQ)[\s\-\u2010-\u2015]*)?(AF|AS|CO|CT|CV|DR|ER|FM|IN|MP|PB|PI|PM|PS|PT|RC|RF|RM|RO|RT|SH|SP|ST|UC|US|UV|WC|WH)[\s\-\u2010-\u2015]*(\d{5}[A-Z]?)$/i;
  const isPartQuery = (query) => partPattern.test(String(query || '').trim());
  const normalizePartQuery = (query) => {
    const raw = String(query || '').trim();
    const match = raw.match(partPattern);
    return match ? 'CQE-' + match[1].toUpperCase() + '-' + match[2].toUpperCase() : raw;
  };
  const intentForQuery = (query) => {
    const raw = String(query || '').trim();
    if (/\b(leak(?:s|ing)?|drip(?:s|ping)?|troubleshoot(?:ing)?|repair(?:s)?|broken|support|not working|no water|low (?:water )?(?:flow|pressure))\b/i.test(raw)) return 'support';
    if (isPartQuery(raw) || /\b(replace(?:ment|ments)?|refills?|cartridges?|membranes?|part(?:s| number)?|model(?: number)?)\b/i.test(raw)) return 'replacement';
    return '';
  };
  const queryFromURL = (href) => {
    try {
      const url = new URL(href);
      // App URLs can keep state after # as well as in the normal query string.
      const hash = url.hash.replace(/^#\??/, '');
      const hashParams = new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash);
      return hashParams.has('q') ? hashParams.get('q') : (url.searchParams.get('q') || '');
    } catch (_) { return ''; }
  };
  /* r3 intent matching (AJ 2026-10-14 "better intent matching"). Both lists are header settings (sections/cq-header.liquid,
     Search rewrites and Search best matches) printed by snippets/cq-search-sheet.liquid as
     <script type="application/json" data-cq-search-rules>; one rule per line, # starts a note.
     Rewrites: "softner, softners => softener" swaps whole words (any case) before predictive search and on submit.
     Best matches: "hard water, scale => /pages/water-softener-quiz | Water softener quiz" puts that link first in the
     results when the query is one of the words, or one of them plus one more word ("pfas filter"). */
  const lines = (text) => String(text || '').split(/\r?\n/).map((l) => l.replace(/#.*$/, '').trim()).filter((l) => l.includes('=>'));
  const words = (list) => list.split(',').map((w) => w.trim().toLowerCase().replace(/\s+/g, ' ')).filter(Boolean);
  const esc = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parseRewrites = (text) => lines(text).map((l) => {
    const [from, to] = l.split('=>');
    const list = words(from);
    return list.length && to.trim() ? { re: new RegExp('(^|[^a-z0-9])(' + list.map(esc).join('|') + ')(?=$|[^a-z0-9])', 'gi'), to: to.trim() } : null;
  }).filter(Boolean);
  const parseShortcuts = (text) => lines(text).map((l) => {
    const [from, rest] = l.split('=>');
    const [url, label, note] = rest.split('|').map((x) => (x || '').trim());
    return /^\/[^\s]*$/.test(url) && label ? { keys: words(from), url, label, note } : null;
  }).filter(Boolean);
  const rewriteQuery = (query, rules) => {
    let q = String(query || '').trim();
    (rules || []).forEach((r) => { q = q.replace(r.re, (m, pre) => pre + r.to); });
    return normalizePartQuery(q.replace(/\s+/g, ' '));
  };
  const shortcutFor = (query, list) => {
    const q = String(query || '').toLowerCase().replace(/[^a-z0-9.]+/g, ' ').trim();
    if (!q || isPartQuery(query)) return null;
    const n = q.split(' ').length;
    return (list || []).find((s) => s.keys.some((k) => q === k || (n <= k.split(' ').length + 1 && (' ' + q + ' ').includes(' ' + k + ' ')))) || null;
  };
  let rules = null;
  const config = () => {
    if (rules) return rules;
    let data = {};
    try { data = JSON.parse(globalThis.document?.querySelector('script[data-cq-search-rules]')?.textContent || '{}'); } catch (_) { /* no rules */ }
    rules = { rewrites: parseRewrites(data.rewrites), shortcuts: parseShortcuts(data.shortcuts) };
    return rules;
  };
  const rewrite = (query) => rewriteQuery(query, config().rewrites);
  const shortcut = (query) => shortcutFor(query, config().shortcuts);
  return { isPartQuery, normalizePartQuery, intentForQuery, queryFromURL, parseRewrites, parseShortcuts, rewriteQuery, shortcutFor, rewrite, shortcut };
});
